<style lang="less" scoped>
  @import "../../../../style/theme.less";

  div.report-block-content {
    border: 1px solid #303030;
    border-radius: @border-radius-base;
    margin: 6px 0;
    padding: 6px;
  }
</style>

<template>
  <div>
    <a-page-header
      @back="() => $router.go(-1)"
      title="Submission Details"
      :sub-title="id"
      style="padding: 0; margin-bottom: 8px"
    >
      <template slot="extra">
        <a-button type="primary" shape="circle" icon="reload" @click="update" />
      </template>
    </a-page-header>

    <a-row :gutter="[16, 16]">
      <a-col :span="12">
        <a-descriptions title="Submission Information" bordered :column="1" ref="submissionInformation">
          <a-descriptions-item label="ID">{{ submissionDetails.id }}</a-descriptions-item>
          <a-descriptions-item label="License Key">{{ submissionDetails.licenseKey }}</a-descriptions-item>
          <a-descriptions-item label="Name">{{ submissionDetails.name }}</a-descriptions-item>
          <a-descriptions-item label="Age">{{ submissionDetails.age }}</a-descriptions-item>
          <a-descriptions-item label="Gender">
            {{ constants.genders.genderDescriptors[submissionDetails.gender] || submissionDetails.gender | capitalise }}
          </a-descriptions-item>
          <a-descriptions-item label="Quiz Type">
            {{ constants.report.typeDescriptors[submissionDetails.reportType] || submissionDetails.reportType | capitalise }}
          </a-descriptions-item>
          <a-descriptions-item label="Completed">
            <a-icon :type="submissionDetails.completed ? 'check' : 'close'" />
          </a-descriptions-item>
          <a-descriptions-item label="Created At">{{ submissionDetails.createdAt | timestamp }}</a-descriptions-item>
          <a-descriptions-item label="Updated At">{{ submissionDetails.updatedAt | timestamp }}</a-descriptions-item>
        </a-descriptions>
      </a-col>

      <a-col :span="12">
        <div class="ant-descriptions-title">Answers</div>
        <a-table
          :columns="columns"
          :data-source="formattedAnswers"
          :pagination="false"
          :rowKey="({ id }) => id"
          style="overflow-y: auto"
          :scroll="{ y: answersHeight }"
        >
        </a-table>
      </a-col>
    </a-row>

    <a-row v-if="submissionDetails.completed" :gutter="[16, 16]">
      <div class="spacer-lg" />

      <a-divider orientation="left">
        Submission Report Information
        <a-divider type="vertical" />
        <a @click="() => showReportPopup = true">View Report</a>
      </a-divider>

      <a-col :span="10">
        <a-descriptions
          title="Archetype"
          bordered
          :column="1"
          v-if="reportDetails.reportType"
        >
          <a-descriptions-item label="ID">
            <a @click="$router.push({ name: 'admin.report-types.details', params: { id: reportDetails.reportType.id }})">
              {{ reportDetails.reportType.id }}
            </a>
          </a-descriptions-item>
          <a-descriptions-item label="Name">{{ reportDetails.reportType.descriptor }}</a-descriptions-item>
          <a-descriptions-item label="Type">{{ reportDetails.reportType.type | capitalise }}</a-descriptions-item>
        </a-descriptions>
      </a-col>

      <a-col :span="14">
        <div class="ant-descriptions-title">Scored Personality Traits</div>

        <a-collapse>
          <a-collapse-panel
            v-for="trait in reportDetails.scoredTraits"
            :key="trait.traitId"
            :header="`Personality Trait: ${resolvePersonalityTrait()(trait.traitId).descriptor}`"
          >
            <a-descriptions
              bordered
              :column="2"
            >
              <a-descriptions-item label="Trait Score">{{ trait.score | round(2) }}</a-descriptions-item>
              <a-descriptions-item label="Domain Scale Score">{{ trait.domainScale | round(2) }}</a-descriptions-item>
              <a-descriptions-item label="Indexed Values" :span="2">
                {{ trait.indexedValues.join(', ') }}
              </a-descriptions-item>
              <a-descriptions-item label="Weight ID" :span="2">
                <a
                  @click="$router.push({ name: 'admin.normalisation-vectors.details', params: { id: trait.weights.normalisationVectorId, weightId: trait.weights.id }})"
                >
                {{ trait.weights.id }}
                </a>
              </a-descriptions-item>
              <a-descriptions-item label="Dividend">
                {{ trait.weights.dividend }}
              </a-descriptions-item>
              <a-descriptions-item label="Divisor">
                {{ trait.weights.divisor }}
              </a-descriptions-item>
            </a-descriptions>
          </a-collapse-panel>
        </a-collapse>
      </a-col>
    </a-row>

    <a-modal
      v-model="showReportPopup"
      :closable="true"
      :title="`Report ${submissionDetails.id}`"
      width="80%"
    >
      <a-divider orientation="left">Report Blocks</a-divider>
      <a-collapse>
        <a-collapse-panel
          v-for="block in reportDetails.reportBlocks"
          :key="block.id"
          :header="`Index: ${block.index} - Name: ${block.descriptor}`"
        >
          <div v-html="block.content" />
          <a-icon
            slot="extra"
            type="setting"
            @click="$router.push({ name: 'admin.report-types.details', params: { id: block.reportTypeId, editBlockId: block.id }})"
          />
        </a-collapse-panel>
      </a-collapse>

      <template slot="footer">
        <a-button key="back" @click="() => showReportPopup = false">
          Close
        </a-button>
      </template>
    </a-modal>

  </div>
</template>

<script>

import { mapState, mapActions, mapGetters } from 'vuex';

const columns = [
  {
    title: 'Question',
    dataIndex: 'questionText',
  }, {
    title: 'Answer Value',
    dataIndex: 'value',
  },
];

export default {
  name: 'admin.submissions.details',

  data: () => ({
    id: null,
    showReportPopup: false,
    answersHeight: '450px',

    columns,
  }),

  created() {
    window.addEventListener('resize', () => this.updateAnswersMaxHeight());
  },
  destroyed() {
    window.removeEventListener('resize', () => this.updateAnswersMaxHeight());
  },

  mounted() {
    const id = this.$route.params.id;
    if (!id) {
      this.$router.push({ name: 'admin.submissions', });
    }

    setTimeout(() => {
      this.updateAnswersMaxHeight();
    }, 500);

    this.id = id;
    return this.update();
  },

  computed: {
    ...mapState('admin', ['submissionDetails', 'reportDetails', 'constants',]),

    formattedAnswers() {
      if (!this.submissionDetails.answers) {
        return [];
      }

      return this.submissionDetails.answers.map(({ id, value, question: { questionText, }, }) => {
        return { id, value, questionText, };
      });
    },
  },

  methods: {
    ...mapActions('admin', ['getSubmissionDetails', 'getReportDetails', 'listPersonalityTraits', 'listConstants',]),
    ...mapGetters('admin', ['resolvePersonalityTrait',]),

    updateAnswersMaxHeight() {
      let maxHeight = 450;
      if (this.$refs.submissionInformation) {
        maxHeight = this.$refs.submissionInformation.$el.clientHeight - 97;
      }

      this.answersHeight = maxHeight;
    },

    update() {
      const id = this.id;
      return Promise.all([
        this.getSubmissionDetails(id),
        this.listConstants(),
      ])
      .then(([submissionDetails,])=> {
        if (submissionDetails.completed) {
          return Promise.all([
            this.getReportDetails(id),
            this.listPersonalityTraits(),
          ]);
        }
      });
    },
  },
};
</script>
